import React from 'react';
import { useResponseFormatting } from './ResponseFormattingContext';
import { CodeBlock } from './CodeBlock';
import { MathBlock } from './MathBlock';
import { TableBlock } from './TableBlock';
import { LinkRenderer, autoLinkText } from './LinkRenderer';
import { JsonBlock } from './JsonBlock';
import { EmailDraftRenderer } from '../email/EmailDraftRenderer';

interface EnhancedMessageContentProps {
  content: string;
  isUser: boolean;
}

export const EnhancedMessageContent: React.FC<EnhancedMessageContentProps> = ({ content, isUser }) => {
  const { settings } = useResponseFormatting();

  // Get font size and density classes
  const getFontSizeClass = () => {
    switch (settings.fontSize) {
      case 'small': return 'text-sm';
      case 'large': return 'text-lg';
      default: return 'text-base';
    }
  };

  const getDensityClass = () => {
    switch (settings.messageDensity) {
      case 'compact': return 'space-y-1 leading-tight';
      case 'spacious': return 'space-y-4 leading-relaxed';
      default: return 'space-y-2 leading-normal';
    }
  };

  // Helper function for inline formatting
  const formatInlineText = (text: string) => {
    // Handle bold text - support both **text** and *text*
    const parts = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g);
    return parts.map((part, partIndex) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={partIndex}>{part.slice(2, -2)}</strong>;
      }
      if (part.startsWith('*') && part.endsWith('*') && part.length > 2) {
        return <strong key={partIndex}>{part.slice(1, -1)}</strong>;
      }
      
      // Handle inline code
      if (part.includes('`')) {
        const codeParts = part.split(/(`[^`]+`)/g);
        return codeParts.map((codePart, codeIndex) => {
          if (codePart.startsWith('`') && codePart.endsWith('`')) {
            return (
              <code key={codeIndex} className="px-1 py-0.5 bg-muted rounded">
                {codePart.slice(1, -1)}
              </code>
            );
          }
          return settings.autoDetectLinks ? autoLinkText(codePart) : codePart;
        });
      }
      
      return settings.autoDetectLinks ? autoLinkText(part) : part;
    });
  };

  // Helper function to validate JSON
  const isValidJson = (str: string): boolean => {
    if (!str || typeof str !== 'string') return false;
    try {
      const parsed = JSON.parse(str);
      return typeof parsed === 'object' && parsed !== null;
    } catch (e) {
      return false;
    }
  };

  // Enhanced content formatting with comprehensive preprocessing
  const formatContent = (text: string) => {
    // Step 1: Check for email draft content - enhanced detection
    if (text.includes('📧 **Email Draft Created**') || text.includes('"type":"email"')) {
      try {
        // Try parsing structured email message format first
        if (text.includes('"type":"email"')) {
          const jsonMatch = text.match(/\{[^}]*"type"\s*:\s*"email"[^}]*\}/);
          if (jsonMatch) {
            const emailData = JSON.parse(jsonMatch[0]);
            if (emailData.data) {
              return [
                <EmailDraftRenderer
                  key="email-draft"
                  draft={emailData.data}
                  showActions={false}
                />
              ];
            }
          }
        }
        
        // Fallback to markdown-style parsing
        const emailMatch = text.match(/\*\*To:\*\* (.+)\n\*\*Subject:\*\* (.+)\n\n\*\*Content:\*\*\n([\s\S]+)/);
        if (emailMatch) {
          const [, to, subject, body] = emailMatch;
          return [
            <EmailDraftRenderer
              key="email-draft"
              draft={{ to: to.trim(), subject: subject.trim(), body: body.trim() }}
              showActions={false}
            />
          ];
        }
      } catch (e) {
        // Continue with normal processing if email parsing fails
      }
    }

    // Step 2: Preprocess HTML tags
    let processedText = text
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>');

    // Step 3: Detect and extract markdown tables before line processing
    const tableRegex = /(?:^|\n)((?:[^\n]*\|[^\n]*\n){2,})/gm;
    const tableMatches = processedText.match(tableRegex);
    
    if (tableMatches) {
      for (const tableMatch of tableMatches) {
        const cleanTable = tableMatch.trim();
        const lines = cleanTable.split('\n');
        
        // Check if it's a valid markdown table (header + separator + at least one row)
        if (lines.length >= 3 && lines[1].includes('|') && lines[1].includes('-')) {
          return [<TableBlock key="table-block" content={cleanTable} />];
        }
      }
    }

    // Step 4: Detect large multi-line JSON blocks
    const jsonBlockMatches = processedText.match(/(\{[\s\S]*?\})/g);
    if (jsonBlockMatches) {
      for (const match of jsonBlockMatches) {
        // Check if this looks like a JSON object with multiple properties
        if (match.includes(':') && match.includes(',') && match.split('\n').length > 3) {
          if (isValidJson(match.trim())) {
            try {
              const jsonData = JSON.parse(match.trim());
              // If it's a complex object with multiple keys, render as JsonBlock
              if (typeof jsonData === 'object' && jsonData !== null && Object.keys(jsonData).length > 2) {
                return [<JsonBlock key="json-block" data={jsonData} title="JSON Response" />];
              }
            } catch (e) {
              // Continue with normal processing if parsing fails
            }
          }
        }
      }
    }

    // Step 5: Process lines using the preprocessed text
    const lines = processedText.split('\n');
    const elements: JSX.Element[] = [];
    let currentParagraph: string[] = [];
    let currentList: { items: string[], isNumbered: boolean } | null = null;
    
    const flushParagraph = () => {
      if (currentParagraph.length > 0) {
        elements.push(
          <div key={`para-${elements.length}`} className="mb-3">
            {currentParagraph.map((line, idx) => (
              <p key={idx} className={idx > 0 ? "mt-1" : ""}>
                {formatInlineText(line)}
              </p>
            ))}
          </div>
        );
        currentParagraph = [];
      }
    };
    
    const flushList = () => {
      if (currentList && currentList.items.length > 0) {
        const ListTag = currentList.isNumbered ? 'ol' : 'ul';
        const listClass = currentList.isNumbered 
          ? "list-decimal list-outside space-y-2 pl-6 ml-2" 
          : "list-disc list-outside space-y-2 pl-6 ml-2";
        
        elements.push(
          <div key={`list-${elements.length}`} className="mb-3">
            <ListTag className={listClass}>
              {currentList.items.map((item, idx) => (
                <li key={idx} className="ml-0">
                  {formatInlineText(item)}
                </li>
              ))}
            </ListTag>
          </div>
        );
        currentList = null;
      }
    };

    lines.forEach((line, index) => {
      const trimmedLine = line.trim();
      
      // Skip empty lines
      if (!trimmedLine) {
        flushParagraph();
        flushList();
        return;
      }

      // Check for code blocks and JSON blocks
      if (trimmedLine.includes('```')) {
        flushParagraph();
        flushList();
        const codeMatch = line.match(/```(\w+)?\n?([\s\S]*?)```/);
        if (codeMatch) {
          const language = codeMatch[1] || 'text';
          const code = codeMatch[2];
          
          // Check if it's JSON and render with JsonBlock
          if (language === 'json' || (language === 'text' && isValidJson(code))) {
            try {
              const jsonData = JSON.parse(code);
              elements.push(<JsonBlock key={elements.length} data={jsonData} title="JSON Response" />);
              return;
            } catch (e) {
              // Fall back to regular code block if JSON parsing fails
            }
          }
          
          elements.push(<CodeBlock key={elements.length} code={code} language={language} />);
          return;
        }
      }

      // Check for inline JSON objects (not in code blocks)
      if (trimmedLine.trim().startsWith('{') && trimmedLine.trim().endsWith('}')) {
        const possibleJson = trimmedLine.trim();
        if (isValidJson(possibleJson)) {
          flushParagraph();
          flushList();
          try {
            const jsonData = JSON.parse(possibleJson);
            elements.push(<JsonBlock key={elements.length} data={jsonData} title="JSON Data" />);
            return;
          } catch (e) {
            // Fall back to regular text if parsing fails
          }
        }
      }

      // Check for headings
      if (trimmedLine.startsWith('###')) {
        flushParagraph();
        flushList();
        const headingText = trimmedLine.replace(/^#+\s*/, '');
        elements.push(
          <h3 key={elements.length} className="font-semibold text-lg mb-2 mt-4">
            {formatInlineText(headingText)}
          </h3>
        );
        return;
      }

      // Check for bullet points
      const bulletMatch = trimmedLine.match(/^[•\-\*]\s*(.+)$/);
      const numberedMatch = trimmedLine.match(/^\d+\.\s*(.+)$/);
      
      if (bulletMatch || numberedMatch) {
        flushParagraph();
        
        const content = (bulletMatch || numberedMatch)?.[1] || '';
        const isNumbered = !!numberedMatch;
        
        if (!currentList || currentList.isNumbered !== isNumbered) {
          flushList();
          currentList = { items: [], isNumbered };
        }
        
        currentList.items.push(content);
        return;
      }

      // Regular text line
      flushList();
      currentParagraph.push(trimmedLine);
    });
    
    // Flush any remaining content
    flushParagraph();
    flushList();
    
    return elements;
  };

  // Safe formatting with fallback
  const renderFormattedContent = () => {
    try {
      const formattedElements = formatContent(content);
      if (formattedElements && formattedElements.length > 0) {
        return formattedElements;
      }
      // Fallback to simple formatting if formatContent returns empty
      return <div className="whitespace-pre-wrap">{content}</div>;
    } catch (error) {
      console.error('Message formatting error:', error);
      // Fallback to plain text with basic formatting
      return <div className="whitespace-pre-wrap">{content}</div>;
    }
  };

  return (
    <div className={`${getFontSizeClass()} ${getDensityClass()}`}>
      {isUser ? (
        <div className="whitespace-pre-wrap">{content}</div>
      ) : (
        <div>
          {renderFormattedContent()}
        </div>
      )}
    </div>
  );
};